import { useMemo, useEffect, useState } from "react";
import axios from "axios";
import { 
  CircularProgress, 
  Pagination, 
  Box, 
  Chip, 
  Modal,
  Typography,
  IconButton,
  Card,
  CardContent,
  Fade,
  Backdrop,
  Switch,
  FormControlLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  TextField,
  Grid,
  CardHeader,
  Divider
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import CloseIcon from '@mui/icons-material/Close';
import DomainIcon from "@mui/icons-material/Domain";
import PersonIcon from "@mui/icons-material/Person";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import BusinessIcon from "@mui/icons-material/Business";
import LanguageIcon from "@mui/icons-material/Language";
import EditIcon from '@mui/icons-material/Edit';
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";
import SoftInput from "components/SoftInput";
import CustomdateD from "../Dashboard/CustomdateD";
import dayjs from "dayjs";
import { LAMBDA_BASE_URL } from "../../../constants";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryParams } from "context/QueryParamsContext";
import { enUS } from "date-fns/locale";
import { useDomain } from "context/DomainContext";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";

function AllUsersTable({
  entriesPerPageOptions = [5, 10, 15, 20, 25],
  canSearch = true,
  date = true,
  isSorted = true,
}) {
  const columns = [
    { Header: "Email", accessor: "email", align: "left" },
    { Header: "Name", accessor: "name", align: "left" },
    { Header: "Whitelabel User", accessor: "is_whitelabel_user", align: "center" },
    { Header: "Domains", accessor: "domain_names", align: "left" },
    { Header: "Created At", accessor: "created_at", align: "left" },
    { Header: "Actions", accessor: "actions", align: "center" },
  ];

  const [rows, setRows] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [page, setPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(entriesPerPageOptions[0] || 10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "created_at",
    direction: "desc",
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUserDomains, setSelectedUserDomains] = useState([]);
  const [selectedUserInfo, setSelectedUserInfo] = useState(null);
  const [logoModalOpen, setLogoModalOpen] = useState(false);
  const [whitelabelModalOpen, setWhitelabelModalOpen] = useState(false);
  const [selectedUserForLogo, setSelectedUserForLogo] = useState(null);
  const [selectedUserForWhitelabel, setSelectedUserForWhitelabel] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [whitelabelForm, setWhitelabelForm] = useState({
    companyName: "",
    websiteUrl: "",
    customerPortalUrl: "",
    logo: null,
    existingLogoUrl: null
  });
  const [whitelabelLoading, setWhitelabelLoading] = useState(false);
  const [whitelabelError, setWhitelabelError] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasExistingSettings, setHasExistingSettings] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { dateRange, setDateRange } = useQueryParams();
  const { domainId } = useDomain();

  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const start = (page - 1) * entriesPerPage + 1;
  const end = Math.min(page * entriesPerPage, totalEntries);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
      setSearchLoading(false);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [search]);

  const fetchAllUsers = async () => {
    if (!domainId) return;

    setLoading(true);
    try {
      const { start_date, end_date } = dateRange;
      const offset = (page - 1) * entriesPerPage;

      const res = await axios.get(`${LAMBDA_BASE_URL}/all-users`, {
        params: {
          limit: entriesPerPage,
          offset,
          search: debouncedSearch,
          sort_key: sortConfig.key,
          sort_direction: sortConfig.direction,
          start_date,
          end_date,
        },
        headers: { "x-api-key": process.env.REACT_APP_API_KEY },
      });

      const formattedRows = res.data.result.users.map((user) => ({
        id: user.id,
        email: user.email,
        name: user.name,
        is_whitelabel_user: user.is_whitelabel_user,
        domain_names: user.domain_names || [],
        domain_details: user.domain_details || [],
        created_at: user.created_at,
        whitelabel_settings: user.whitelabel_settings || null
      }));

      setRows(formattedRows);
      setTotalEntries(res.data.result.total);
    } catch (err) {
      console.error("Error fetching all users", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (domainId) {
      fetchAllUsers();
    }
  }, [page, entriesPerPage, debouncedSearch, sortConfig, dateRange, domainId]);

  const sortedRows = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    return [...rows].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (sortConfig.key === "domain_names") {
        const aDomains = aVal?.length || 0;
        const bDomains = bVal?.length || 0;
        return sortConfig.direction === "asc" ? aDomains - bDomains : bDomains - aDomains;
      }

      if (sortConfig.key === "is_whitelabel_user") {
        return sortConfig.direction === "asc"
          ? (aVal ? 1 : -1) - (bVal ? 1 : -1)
          : (bVal ? 1 : -1) - (aVal ? 1 : -1);
      }

      if (sortConfig.key === "created_at") {
        return sortConfig.direction === "asc"
          ? new Date(aVal) - new Date(bVal)
          : new Date(bVal) - new Date(aVal);
      }

      if (aVal === bVal) return 0;
      return sortConfig.direction === "asc"
        ? aVal > bVal
          ? 1
          : -1
        : aVal < bVal
        ? 1
        : -1;
    });
  }, [rows, sortConfig]);

  const checkUserHasWhitelabelSettings = (user) => {
    console.log('Checking whitelabel settings for user:', user);
    return user.whitelabel_settings && (
      user.whitelabel_settings.company_name ||
      user.whitelabel_settings.website_url ||
      user.whitelabel_settings.customer_portal_url ||
      user.whitelabel_settings.company_logo
    );
  };

  const loadUserWhitelabelSettings = (user) => {
    if (!user.whitelabel_settings) {
      return false;
    }

    const settings = user.whitelabel_settings;
    
    setWhitelabelForm({
      companyName: settings?.company_name || "",
      websiteUrl: settings?.website_url || "",
      customerPortalUrl: settings?.customer_portal_url || "",
      logo: null,
      existingLogoUrl: settings?.company_logo ? `data:image/png;base64,${settings.company_logo}` : null
    });
    
    return true;
  };

  const handleEditWhitelabel = (user) => {
    setSelectedUserForWhitelabel(user);
    setIsEditMode(true);
    
    const hasSettings = checkUserHasWhitelabelSettings(user);
    setHasExistingSettings(hasSettings);
    
    if (hasSettings) {
      loadUserWhitelabelSettings(user);
    } else {
      setWhitelabelForm({
        companyName: "",
        websiteUrl: "",
        customerPortalUrl: "",
        logo: null,
        existingLogoUrl: null
      });
    }
    
    setWhitelabelModalOpen(true);
  };

  const enableWhitelabel = async (userId) => {
    try {
      const response = await axios.patch(`${LAMBDA_BASE_URL}/user-whitelabel-status`, {
        user_id: userId,
        is_whitelabel_user: true
      }, {
        headers: { "x-api-key": process.env.REACT_APP_API_KEY }
      });

      if (response.data.statusCode === 200) {
        await fetchAllUsers();
      } else {
        console.error('Failed to enable whitelabel status:', response.data.message);
      }
    } catch (error) {
      console.error('Error enabling whitelabel:', error);
    }
  };

  const disableWhitelabel = async (userId) => {
    try {
      const response = await axios.patch(`${LAMBDA_BASE_URL}/user-whitelabel-status`, {
        user_id: userId,
        is_whitelabel_user: false
      }, {
        headers: { "x-api-key": process.env.REACT_APP_API_KEY }
      });

      if (response.data.statusCode === 200) {
        await fetchAllUsers();
      } else {
        console.error('Failed to disable whitelabel status:', response.data.message);
      }
    } catch (error) {
      console.error('Error disabling whitelabel:', error);
    }
  };

  const handleWhitelabelToggle = async (user, newValue) => {
    try {
      if (newValue) {
        const hasSettings = checkUserHasWhitelabelSettings(user);
        
        if (hasSettings) {
          await enableWhitelabel(user.id);
        } else {
          setSelectedUserForWhitelabel(user);
          setIsEditMode(false);
          setHasExistingSettings(false);
          setWhitelabelForm({
            companyName: "",
            websiteUrl: "",
            customerPortalUrl: "",
            logo: null,
            existingLogoUrl: null
          });
          setWhitelabelModalOpen(true);
        }
      } else {
        await disableWhitelabel(user.id);
      }
    } catch (error) {
      console.error('Error updating whitelabel status:', error);
    }
  };

  const handleWhitelabelFormChange = (field, value) => {
    setWhitelabelForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleWhitelabelLogoSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setWhitelabelError('Please select a valid image file');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        setWhitelabelError('File size must be less than 5MB');
        return;
      }

      setWhitelabelForm(prev => ({
        ...prev,
        logo: file,
        existingLogoUrl: null
      }));
      setWhitelabelError("");
    }
  };

  const handleSaveWhitelabel = async () => {
    if (!selectedUserForWhitelabel) return;

    setWhitelabelLoading(true);
    setWhitelabelError("");

    try {
      await enableWhitelabel(selectedUserForWhitelabel.id);

      const payload = {
        user_id: selectedUserForWhitelabel.id,
        company_name: whitelabelForm.companyName || null,
        website_url: whitelabelForm.websiteUrl || null,
        customer_portal_url: whitelabelForm.customerPortalUrl || null,
        is_whitelabel_user: true
      };

      if (whitelabelForm.logo) {
        const base64Logo = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const base64 = e.target.result.split(',')[1];
            resolve(base64);
          };
          reader.readAsDataURL(whitelabelForm.logo);
        });
        payload.company_logo = base64Logo;
      }

      const whitelabelSettingsResponse = await axios.patch(
        `${LAMBDA_BASE_URL}/user-whitelabel-settings`,
        payload,
        {
          headers: { 
            "x-api-key": process.env.REACT_APP_API_KEY,
            "Content-Type": "application/json"
          }
        }
      );

      if (whitelabelSettingsResponse.data.statusCode === 200) {
        if (selectedUserForWhitelabel.domain_details && selectedUserForWhitelabel.domain_details.length > 0) {
          const domainTokens = selectedUserForWhitelabel.domain_details.map(domain => domain.domain_token);
          
          const updatePromises = domainTokens.map(async (domainToken) => {
            const settingsUpdate = {
            };
            
            if (whitelabelForm.companyName) {
              settingsUpdate.whitelabel_company_name = whitelabelForm.companyName;
            }
            if (whitelabelForm.websiteUrl) {
              settingsUpdate.url = whitelabelForm.websiteUrl;
            }
            if (whitelabelForm.customerPortalUrl) {
              settingsUpdate.whitelabel_company_portal_url = whitelabelForm.customerPortalUrl;
            }
            if (whitelabelForm.logo) {
              const reader = new FileReader();
              return new Promise((resolve) => {
                reader.onload = async (e) => {
                  settingsUpdate.company_logo = e.target.result;
                  try {
                    const response = await axios.patch(`${LAMBDA_BASE_URL}/settings`, settingsUpdate, {
                      headers: {
                        'domain-token': domainToken,
                        'x-api-key': process.env.REACT_APP_API_KEY
                      }
                    });
                    resolve(response);
                  } catch (error) {
                    console.error('Error updating domain settings:', error);
                    resolve({ error });
                  }
                };
                reader.readAsDataURL(whitelabelForm.logo);
              });
            } else {
              return axios.patch(`${LAMBDA_BASE_URL}/settings`, settingsUpdate, {
                headers: {
                  'domain-token': domainToken,
                  'x-api-key': process.env.REACT_APP_API_KEY
                }
              });
            }
          });

          const results = await Promise.allSettled(updatePromises);
          const allSuccessful = results.every(result => 
            result.status === 'fulfilled' && 
            result.value.data && 
            result.value.data.success
          );

          if (!allSuccessful) {
            console.warn('Failed to update some domain settings');
          }
        }

        await fetchAllUsers();
        setWhitelabelModalOpen(false);
        resetWhitelabelForm();
      } else {
        setWhitelabelError(whitelabelSettingsResponse.data.message || 'Failed to save whitelabel settings');
      }
    } catch (error) {
      console.error('Error saving whitelabel settings:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      setWhitelabelError(error.response?.data?.message || 'Failed to save whitelabel settings. Please try again.');
    } finally {
      setWhitelabelLoading(false);
    }
  };

  const resetWhitelabelForm = () => {
    setWhitelabelForm({
      companyName: "",
      websiteUrl: "",
      customerPortalUrl: "",
      logo: null,
      existingLogoUrl: null
    });
    setWhitelabelError("");
    setSelectedUserForWhitelabel(null);
    setIsEditMode(false);
    setHasExistingSettings(false);
  };

  const handleCloseWhitelabelModal = () => {
    setWhitelabelModalOpen(false);
    setTimeout(resetWhitelabelForm, 200);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setUploadError('Please select a valid image file');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        setUploadError('File size must be less than 5MB');
        return;
      }

      setSelectedFile(file);
      setUploadError("");
    
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadLogo = async () => {
    if (!selectedFile || !selectedUserForLogo) {
      setUploadError('Please select a logo file');
      return;
    }

    setUploading(true);
    setUploadError("");

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64String = e.target.result;
        
        const domainTokens = selectedUserForLogo.domain_details.map(domain => domain.domain_token);
        const updatePromises = domainTokens.map(async (domainToken) => {
          return axios.patch(`${LAMBDA_BASE_URL}/settings`, {
            company_logo: base64String,
            // is_whitelabelled: true
          }, {
            headers: {
              'domain-token': domainToken,
              'x-api-key': process.env.REACT_APP_API_KEY
            }
          });
        });

        const results = await Promise.allSettled(updatePromises);
        
        const allSuccessful = results.every(result => result.status === 'fulfilled' && result.value.data.success);
        
        if (allSuccessful) {
          await fetchAllUsers();
          setLogoModalOpen(false);
          resetLogoModal();
        } else {
          setUploadError('Failed to update logo for some domains');
        }
      };
      
      reader.readAsDataURL(selectedFile);

    } catch (error) {
      console.error('Error uploading logo:', error);
      setUploadError('Failed to upload logo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const resetLogoModal = () => {
    setSelectedFile(null);
    setPreviewUrl("");
    setUploadError("");
    setSelectedUserForLogo(null);
  };

  const handleCloseLogoModal = () => {
    setLogoModalOpen(false);
    setTimeout(resetLogoModal, 200);
  };

  const handlePageChange = (event, value) => setPage(value);

  const handleEntriesPerPageChange = ({ value }) => {
    setEntriesPerPage(value);
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setSearchLoading(true);
  };

  const handleSort = (columnKey) => {
    setSortConfig((prev) => ({
      key: columnKey,
      direction:
        prev.key === columnKey
          ? prev.direction === "desc"
            ? "asc"
            : "desc"
          : "asc",
    }));
  };

  const handleDateRangeSelect = ({ start, end }) => {
    setDateRange({ start_date: start, end_date: end });
    sessionStorage.setItem("saved_start_date", start);
    sessionStorage.setItem("saved_end_date", end);
    const queryParams = new URLSearchParams(location.search);
    queryParams.set("start_date", start);
    queryParams.set("end_date", end);
    navigate({ pathname: location.pathname, search: queryParams.toString() });
  };

  const handleViewAllDomains = (user) => {
    setSelectedUserDomains(user.domain_details || []);
    setSelectedUserInfo({
      name: user.name,
      email: user.email,
      referral_code: user.referral_code,
      is_whitelabel_user: user.is_whitelabel_user
    });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setTimeout(() => {
      setSelectedUserDomains([]);
      setSelectedUserInfo(null);
    }, 200);
  };

  const renderDomainNames = (domainNames, domainDetails, user) => {
    if (!domainNames || domainNames.length === 0) {
      return (
        <SoftTypography variant="caption" color="textSecondary" fontStyle="italic">
          No domains
        </SoftTypography>
      );
    }

    return (
      <SoftBox display="flex" flexWrap="wrap" gap={0.5} maxWidth="300px">
        {domainNames.slice(0, 3).map((domain, index) => (
          <Chip
            key={index}
            label={domain}
            size="small"
            variant="outlined"
            sx={{
              fontSize: '0.75rem',
              height: '24px',
              borderColor: '#219297',
              color: '#219297',
              '& .MuiChip-label': {
                px: 1,
              }
            }}
          />
        ))}
        {domainNames.length > 3 && (
          <Chip
            label={`+${domainNames.length - 3} more`}
            size="small"
            variant="filled"
            onClick={() => handleViewAllDomains(user)}
            sx={{
              fontSize: '0.75rem',
              height: '24px',
              cursor: 'pointer',
              backgroundColor: '#219297',
              color: '#ffffff',
              '& .MuiChip-label': {
                px: 1,
              },
              '&:hover': {
                backgroundColor: '#1a7579',
              }
            }}
          />
        )}
      </SoftBox>
    );
  };

  const getModalTitle = () => {
    if (isEditMode) {
      return 'Edit Whitelabel Settings';
    }
    return hasExistingSettings ? 'Enable Whitelabel' : 'Configure Whitelabel Settings';
  };

  const getSaveButtonText = () => {
    if (isEditMode) {
      return whitelabelLoading ? 'Updating...' : 'Update Settings';
    }
    return whitelabelLoading ? 'Saving...' : (hasExistingSettings ? 'Enable Whitelabel' : 'Save & Enable Whitelabel');
  };

  return (
    <>
      <TableContainer sx={{ boxShadow: "none", minHeight: "400px" }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress sx={{ color: '#219297' }} />
          </Box>
        ) : (
          <>
            <SoftBox
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              flexWrap="wrap"
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
              gap={2}
              p={2}
            >
              <SoftBox display="flex" alignItems="center" sx={{ width: { xs: "100%", md: "auto" } }}>
                <SoftSelect
                  defaultValue={{ value: entriesPerPage, label: entriesPerPage }}
                  options={entriesPerPageOptions.map((entry) => ({ value: entry, label: entry }))}
                  onChange={handleEntriesPerPageChange}
                  size="small"
                />
                <SoftTypography variant="caption" color="secondary" ml={1}>
                  entries per page
                </SoftTypography>
              </SoftBox>

              <SoftBox
                display="flex"
                flexDirection={{ xs: "column", md: "row" }}
                alignItems={{ xs: "stretch", md: "center" }}
                gap={2}
                width={{ xs: "100%", md: "auto" }}
              >
                {canSearch && (
                  <SoftBox sx={{ width: { xs: "100%", md: "12rem" }, position: "relative" }}>
                    <SoftInput
                      placeholder="Search users..."
                      value={search}
                      onChange={handleSearchChange}
                      fullWidth
                    />
                    {searchLoading && (
                      <CircularProgress 
                        size={20} 
                        sx={{ 
                          position: "absolute", 
                          right: "8px", 
                          top: "50%", 
                          transform: "translateY(-50%)",
                          color: '#219297'
                        }} 
                      />
                    )}
                  </SoftBox>
                )}

                {date && (
                  <SoftBox sx={{ width: { xs: "100%", md: "auto" }, ml: { xs: 0, md: 0 } }}>
                    <CustomdateD onRangeSelect={handleDateRangeSelect} />
                  </SoftBox>
                )}
              </SoftBox>
            </SoftBox>

            <Table>
              <SoftBox component="thead">
                {columns?.map((column, key) => (
                  <DataTableHeadCell
                    key={key}
                    width={column.width || "auto"}
                    align={column.align || "left"}
                    onClick={() => handleSort(column.accessor)}
                    sorted={sortConfig.key === column.accessor ? sortConfig.direction : "none"}
                    style={{ cursor: "pointer", userSelect: "none" }}
                  >
                    {column.Header}
                  </DataTableHeadCell>
                ))}
              </SoftBox>

              <TableBody>
                {sortedRows.length > 0 ? (
                  sortedRows.map((row, key) => (
                    <TableRow key={key} sx={{ padding: 4 }}>
                      {columns.map((col, index) => (
                        <DataTableBodyCell key={index} align={col.align || "left"}>
                          {col.accessor === "created_at" ? (
                            dayjs(row[col.accessor]).format("MMM D, YYYY hh:mm A")
                          ) : col.accessor === "domain_names" ? (
                            renderDomainNames(row[col.accessor], row.domain_details, row)
                          ) : col.accessor === "is_whitelabel_user" ? (
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={row[col.accessor] || false}
                                  onChange={(e) => handleWhitelabelToggle(row, e.target.checked)}
                                  color="primary"
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                      color: '#219297',
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                      backgroundColor: '#219297',
                                    },
                                  }}
                                />
                              }
                              label=""
                            />
                          ) : col.accessor === "actions" ? (
                            row.is_whitelabel_user && (
                              <IconButton
                                size="small"
                                onClick={() => handleEditWhitelabel(row)}
                                sx={{
                                  color: '#219297',
                                  '&:hover': {
                                    backgroundColor: 'rgba(33, 146, 151, 0.1)',
                                  }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            )
                          ) : (
                            row[col.accessor] || "-"
                          )}
                        </DataTableBodyCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <DataTableBodyCell colSpan={columns.length} align="center">
                      <SoftTypography variant="body2" color="textSecondary">
                        No users found
                      </SoftTypography>
                    </DataTableBodyCell>
                  </TableRow>
                )}
              </TableBody>

              {totalEntries > 0 && (
                <SoftBox pb={1} display="flex" justifyContent="space-between" alignItems="center" paddingTop="10px" paddingLeft="40px">
                  <SoftTypography variant="button" color="secondary">
                    Showing {new Intl.NumberFormat(enUS).format(start)} to{" "}
                    {new Intl.NumberFormat(enUS).format(end)} of{" "}
                    {new Intl.NumberFormat(enUS).format(totalEntries)} entries
                  </SoftTypography>
                </SoftBox>
              )}
            </Table>

            {totalPages > 0 && (
              <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={2}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  shape="circular"
                  color="info"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      "&.Mui-selected": {
                        background: "#219297",
                        color: "white !important",
                        "&:hover": { background: "#1a7579" },
                      },
                      "&:not(.Mui-selected)": { color: "black" },
                    },
                  }}
                />
                <SoftTypography variant="button">
                  Page {new Intl.NumberFormat(enUS).format(page)} of{" "}
                  {new Intl.NumberFormat(enUS).format(totalPages)}
                </SoftTypography>
              </SoftBox>
            )}
          </>
        )}
      </TableContainer>

      <Dialog open={whitelabelModalOpen} onClose={handleCloseWhitelabelModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #219297 0%, #1a7579 100%)', 
          color: '#ffffff',
          padding: '16px 24px'
        }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight="600" color="#ffffff">
              {getModalTitle()}
            </Typography>
            <IconButton 
              onClick={handleCloseWhitelabelModal} 
              size="small" 
              disabled={whitelabelLoading}
              sx={{ color: '#ffffff' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {selectedUserForWhitelabel && (
            <Box mb={3} p={2} sx={{ backgroundColor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="body2" color="#219297" fontWeight="500">
                {isEditMode ? 'Editing whitelabel for:' : 
                 hasExistingSettings ? 'Enabling whitelabel for:' : 'Configuring whitelabel for:'} 
                <strong>{selectedUserForWhitelabel.name}</strong> ({selectedUserForWhitelabel.email})
              </Typography>
              <Typography variant="caption" color="#219297">
                {hasExistingSettings && !isEditMode ? 
                 'User has existing whitelabel settings. Whitelabel will be enabled with current settings.' :
                 'These settings will be applied to all domains'}
              </Typography>
            </Box>
          )}
          
          {whitelabelError && (
            <Alert severity="error" sx={{ mb: 2, border: '1px solid #ffcdd2' }}>
              {whitelabelError}
            </Alert>
          )}
          {(isEditMode || !hasExistingSettings) && (
            <Box mb={3}>
              <Typography variant="h6" color="#219297" fontWeight="600" gutterBottom>
                Company Information
              </Typography>
              <Typography variant="body2" color="#666" mb={2}>
                Basic company details for whitelabeling (All fields are optional)
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="#219297" fontWeight="500" gutterBottom>
                      Company Name
                    </Typography>
                    <input
                      type="text"
                      value={whitelabelForm.companyName}
                      onChange={(e) => handleWhitelabelFormChange('companyName', e.target.value)}
                      placeholder="Company Name"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="#219297" fontWeight="500" gutterBottom>
                      Website URL
                    </Typography>
                    <input
                      type="text"
                      value={whitelabelForm.websiteUrl}
                      onChange={(e) => handleWhitelabelFormChange('websiteUrl', e.target.value)}
                      placeholder="https://example.com"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box>
                    <Typography variant="body2" color="#219297" fontWeight="500" gutterBottom>
                      Customer Portal URL
                    </Typography>
                    <input
                      type="text"
                      value={whitelabelForm.customerPortalUrl}
                      onChange={(e) => handleWhitelabelFormChange('customerPortalUrl', e.target.value)}
                      placeholder="https://portal.example.com"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      Leave empty to use default Ansera portal
                    </div>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box>
                    <Typography variant="body2" color="#219297" fontWeight="500" gutterBottom>
                      Logo (Optional)
                    </Typography>
                    <Box
                      sx={{
                        border: '2px dashed',
                        borderColor: (whitelabelForm.logo || whitelabelForm.existingLogoUrl) ? '#219297' : '#e0e0e0',
                        borderRadius: 1,
                        p: 3,
                        textAlign: 'center',
                        backgroundColor: (whitelabelForm.logo || whitelabelForm.existingLogoUrl) ? '#f0f9fa' : 'transparent',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: '#219297',
                          backgroundColor: '#f0f9fa',
                        }
                      }}
                      onClick={() => document.getElementById('whitelabel-logo-upload').click()}
                    >
                      <input
                        id="whitelabel-logo-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleWhitelabelLogoSelect}
                        style={{ display: 'none' }}
                      />
                      
                      {whitelabelForm.existingLogoUrl && !whitelabelForm.logo ? (
                        <Box>
                          <img
                            src={whitelabelForm.existingLogoUrl}
                            alt="Current logo"
                            style={{
                              maxWidth: '200px',
                              maxHeight: '100px',
                              objectFit: 'contain',
                              marginBottom: '16px',
                            }}
                          />
                          <Typography variant="body2" color="#219297" fontWeight="500">
                            Current logo - Click to change
                          </Typography>
                        </Box>
                      ) : whitelabelForm.logo ? (
                        <Box>
                          <CloudUploadIcon sx={{ fontSize: 40, color: '#219297', mb: 1 }} />
                          <Typography variant="body2" color="#219297" fontWeight="500">
                            New logo selected - Click to change
                          </Typography>
                          <Typography variant="caption" color="#219297" display="block" mt={1}>
                            Selected: {whitelabelForm.logo.name}
                          </Typography>
                        </Box>
                      ) : (
                        <Box>
                          <CloudUploadIcon sx={{ fontSize: 40, color: '#9e9e9e', mb: 1 }} />
                          <Typography variant="body2" color="#666" fontWeight="500">
                            {isEditMode ? 'No logo uploaded - Click to upload logo' : 'Click to upload logo'}
                          </Typography>
                          <Typography variant="caption" color="#666">
                            PNG, JPG, JPEG up to 5MB
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {hasExistingSettings && !isEditMode && (
            <Alert severity="info" sx={{ 
              backgroundColor: '#e8f5f5', 
              color: '#219297',
              border: '1px solid #219297',
              mb: 3
            }}>
              <Typography variant="body2">
                This user already has whitelabel settings configured. Whitelabel will be enabled with the existing settings.
                You can edit the settings later using the edit button.
              </Typography>
            </Alert>
          )}

          <Alert severity="info" sx={{ 
            backgroundColor: '#e8f5f5', 
            color: '#219297',
            border: '1px solid #219297'
          }}>
            <Typography variant="body2">
              All fields are optional. If left empty, default Ansera values will be used.
            </Typography>
          </Alert>

          {whitelabelLoading && (
            <LinearProgress 
              sx={{ 
                mt: 2,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#219297'
                }
              }} 
            />
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button 
            onClick={handleCloseWhitelabelModal} 
            disabled={whitelabelLoading}
            sx={{
              color: '#219297',
              border: '1px solid #219297',
              '&:hover': {
                backgroundColor: '#f0f9fa',
                border: '1px solid #1a7579'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveWhitelabel}
            variant="contained"
            disabled={whitelabelLoading}
            sx={{
              backgroundColor: '#219297',
              '&:hover': { 
                backgroundColor: '#1a7579' 
              },
              '&:disabled': {
                backgroundColor: '#cccccc'
              },
              color: "#ffffff"
            }}
          >
            {getSaveButtonText()}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={logoModalOpen} onClose={handleCloseLogoModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight="600">
              Upload Whitelabel Logo
            </Typography>
            <IconButton onClick={handleCloseLogoModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedUserForLogo && (
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">
                For user: <strong>{selectedUserForLogo.name}</strong> ({selectedUserForLogo.email})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This logo will be applied to all {selectedUserForLogo.domain_details?.length || 0} domains
              </Typography>
            </Box>
          )}
          
          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uploadError}
            </Alert>
          )}

          <Box mb={3}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Upload logo for whitelabeling (Max 5MB, PNG/JPG/JPEG)
            </Typography>
            
            <Box
              sx={{
                border: '2px dashed',
                borderColor: selectedFile ? '#219297' : '#e0e0e0',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                backgroundColor: selectedFile ? '#f8f9fa' : 'transparent',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: '#219297',
                  backgroundColor: '#f8f9fa',
                }
              }}
              onClick={() => document.getElementById('logo-upload').click()}
            >
              <input
                id="logo-upload"
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              
              {previewUrl ? (
                <Box>
                  <img
                    src={previewUrl}
                    alt="Logo preview"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '100px',
                      objectFit: 'contain',
                      marginBottom: '16px',
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Click to change logo
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <CloudUploadIcon sx={{ fontSize: 48, color: '#9e9e9e', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Click to upload logo
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PNG, JPG, JPEG up to 5MB
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {uploading && <LinearProgress sx={{ mb: 2 }} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLogoModal} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUploadLogo}
            variant="contained"
            disabled={!selectedFile || uploading}
            sx={{
              backgroundColor: '#219297',
              '&:hover': { backgroundColor: '#1a7579' }
            }}
          >
            {uploading ? 'Uploading...' : 'Upload Logo'}
          </Button>
        </DialogActions>
      </Dialog>

      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500, sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' } }}}
      >
        <Fade in={modalOpen}>
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 600, maxWidth: '90vw', maxHeight: '80vh', bgcolor: 'background.paper', boxShadow: '0 20px 60px rgba(33, 146, 151, 0.15)', borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ background: 'linear-gradient(135deg, #219297 0%, #1a7579 100%)', p: 3, color: 'white' }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box flex={1}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="h5" fontWeight="600" color="#ffffff">User Domain Details</Typography>
                  </Box>
                  {selectedUserInfo && (
                    <Box sx={{ mt: 2, opacity: 0.95 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <PersonIcon sx={{ fontSize: 16, color: '#ffffff' }} />
                        <Typography variant="body2" fontWeight="500" color="#ffffff">{selectedUserInfo.name}</Typography>
                      </Box>
                      <Typography variant="caption" sx={{ opacity: 0.9 }} color="#ffffff">{selectedUserInfo.email}</Typography>
                      <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.85 }} color="#ffffff">
                        Whitelabel: {selectedUserInfo.is_whitelabel_user ? 'Yes' : 'No'}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <IconButton onClick={handleCloseModal} size="small" sx={{ color: '#ffffff', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ p: 3, maxHeight: 'calc(80vh - 180px)', overflowY: 'auto' }}>
              {selectedUserDomains.length > 0 ? (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontSize: '0.875rem', fontWeight: 600 }}>
                    Total Domains: {selectedUserDomains.length}
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {selectedUserDomains.map((domain, index) => (
                      <Card key={index} sx={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)', border: '1px solid #e0e0e0', borderRadius: 2, transition: 'all 0.3s ease', '&:hover': { boxShadow: '0 4px 12px rgba(33, 146, 151, 0.15)', borderColor: '#219297', transform: 'translateY(-2px)' } }}>
                        <CardContent sx={{ p: 2.5 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box flex={1}>
                              <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <DomainIcon sx={{ fontSize: 20, color: '#219297' }} />
                                <Typography variant="subtitle1" fontWeight="600" sx={{ color: '#344767' }}>
                                  {domain.domain_name}
                                </Typography>
                              </Box>
                            </Box>
                            <Chip 
                              label={domain.role}
                              size="small"
                              sx={{
                                backgroundColor: domain.role === 'admin' ? '#e8f5f5' : '#f5f5f5',
                                color: domain.role === 'admin' ? '#219297' : '#67748e',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                height: '28px',
                                textTransform: 'capitalize',
                                border: domain.role === 'admin' ? '1px solid #219297' : '1px solid #e0e0e0',
                                '& .MuiChip-label': { px: 1.5 }
                              }}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={4}>
                  <DomainIcon sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary" textAlign="center">No domain details available</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>
    </>
  );
}

export default AllUsersTable;